import {useMemo} from 'react';


/**
 * Custom React hook to generate a unique identifier (UID).
 * 
 * The UID is memoized based on the provided parameters to ensure stability
 * across renders unless one of the dependencies changes.
 *
 * @function useUID
 * @param {string} [prefix='id'] - A string to prepend to the UID.
 * @param {string} [suffix=''] - A string to append to the UID.
 * @param {string} [delimiter='_'] - The delimiter to use between parts of the UID.
 * @param {boolean} [sequential=true] - Whether to use a sequential counter (true) 
 *   or a timestamp-based random identifier (false).
 * @returns {string} The generated unique identifier.
 *
 * @example
 * const id = useUID('button', '', '-', true);
 * // → 'button-s1'
 *
 * @example
 * const randomId = useUID('item', '', '-', false);
 * // → 'item-dr1696628579312.1234'
 */
function useUID(prefix='id', suffix='', delimiter='_', sequential=true){
  const uid = useMemo(() => makeUID(prefix, suffix, delimiter, sequential), [prefix, suffix, delimiter, sequential]);
  return uid;
}

let __count__ = 0;

/**
 * Generates a unique identifier string based on the provided parameters.
 *
 * @function makeUID
 * @param {string} prefix - A string to prepend to the UID.
 * @param {string} suffix - A string to append to the UID.
 * @param {string} delimiter - The delimiter to use between parts of the UID.
 * @param {boolean} sequential - Whether to use a sequential counter (true) 
 *   or a timestamp-based random identifier (false).
 * @returns {string} A unique identifier string.
 *
 * @example
 * makeUID('user', 'input', '-', true);
 * // → 'user-s1-input'
 *
 * @example
 * makeUID('user', 'input', '-', false);
 * // → 'user-dr1696628579312.1234-input'
 */
export function makeUID(prefix, suffix, delimiter, sequential){
  let index = '';
  if (sequential) {
    __count__ += 1;
    index = `s${__count__}`;
  } else {
    index = `dr${new Date().getTime() + Math.random()}`;
  }

  return `${prefix ?? ''}${delimiter ?? ''}${index}${delimiter ?? ''}${suffix ?? ''}`;
}

export default useUID;